import * as React from 'react'

import type { BasePaginationVariables } from 'modules/core/components/DataTable/components/Pagination/types'
import { PAGINATION_KIND } from 'modules/core/components/DataTable/constants'
import type { PaginationType } from 'modules/core/components/DataTable/types'

type Props = {
  children: React.ReactNode
  pagination: PaginationType
}

interface ForwardPaginationVariables {
  first: number
  after?: string
}

interface BackwardPaginationVariables {
  last: number
  before?: string
}

interface PaginationVariables extends BasePaginationVariables {
  first: number
  offsetPage: number
}

export const PaginationStateContext = React.createContext<{
  pagination: PaginationType
  resetPagination: () => void
  getPaginationVariables: () => PaginationVariables | Record<string, never>
  variables: ForwardPaginationVariables | BackwardPaginationVariables
  setVariables: (variables: ForwardPaginationVariables | BackwardPaginationVariables) => void
  currentPage: number
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
} | null>(null)

export function PaginationStateProvider({ children, pagination }: Props) {
  const [variables, setVariables] = React.useState<
    ForwardPaginationVariables | BackwardPaginationVariables
  >({
    first: pagination.itemsPerPage,
  })
  const [currentPage, setCurrentPage] = React.useState(1)

  const resetPagination = () => {
    setCurrentPage?.(1)
    setVariables({ first: pagination.itemsPerPage })
  }

  function getOffsetPaginationVariables(): PaginationVariables | Record<string, never> {
    if (pagination.kind !== PAGINATION_KIND.OFFSET) {
      return {}
    }

    return {
      __paginationKind: PAGINATION_KIND.OFFSET,
      __paginationInfiniteScroll: !!pagination.infiniteScroll,
      first: pagination.itemsPerPage,
      offsetPage: currentPage,
    }
  }

  const getCursorPaginationVariables = () => {
    if (pagination.kind !== PAGINATION_KIND.CURSOR) {
      return {}
    }

    return {
      __paginationKind: PAGINATION_KIND.CURSOR,
      __paginationInfiniteScroll: !!pagination.infiniteScroll,
      ...variables,
    } as const
  }

  const getPaginationVariables = () => ({
    ...getOffsetPaginationVariables(),
    ...getCursorPaginationVariables(),
  })

  const value = {
    pagination,
    resetPagination,
    getPaginationVariables,
    // Cursor
    variables,
    setVariables,
    // Offset
    currentPage,
    setCurrentPage,
  }

  return <PaginationStateContext.Provider value={value}>{children}</PaginationStateContext.Provider>
}
